import React, { useEffect, useState } from "react";
import { Box, Skeleton, SxProps, Theme } from "@mui/material";
import { ImageNotSupportedOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { useDocumentCraftingImage } from "../../../Query/useDocumentCrafting";

interface Props {
  /** API path of the image (`documentCraftingFileUrls`); null shows the
   * fallback. */
  url: string | null | undefined;
  alt: string;
  /** Fetch only once the image comes near the viewport (default). */
  lazy?: boolean;
  /** CSS aspect ratio of the frame, e.g. 210 / 297 for an A4 page. */
  aspectRatio?: number | string;
  fit?: "contain" | "cover";
  /** Shown when there is no image or it failed to load. */
  fallback?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * An image of the module loaded with the bearer token (`<img src>` cannot
 * send it): a skeleton while loading, the fallback on errors.
 */
const AuthImage: React.FC<Props> = ({
  url,
  alt,
  lazy = true,
  aspectRatio,
  fit = "contain",
  fallback,
  sx,
}) => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [inView, setInView] = useState(!lazy);

  useEffect(() => {
    if (inView || !node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, inView]);

  const image = useDocumentCraftingImage(url, inView);

  return (
    <Box
      ref={setNode}
      sx={[
        {
          position: "relative",
          overflow: "hidden",
          aspectRatio,
          backgroundColor: color.gray100,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {image.src ? (
        <Box
          component="img"
          src={image.src}
          alt={alt}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: fit,
          }}
        />
      ) : !url || image.isError ? (
        (fallback ?? (
          <Box
            role="img"
            aria-label={alt}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: color.gray300,
            }}
          >
            <ImageNotSupportedOutlined />
          </Box>
        ))
      ) : (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: "absolute", inset: 0, height: "100%" }}
        />
      )}
    </Box>
  );
};

export default AuthImage;
